/**
 * T400 — the quiet "Driver details" capture block: street address + Eircode
 * per journey end, and per extra-pickup stop when Helen's count is on.
 *
 * Eircode field FIRST (Alex's UX ruling: less typing — and when the Phase-2
 * Autoaddress lookup lands, entering either field will autofill the other, so
 * the order is already the drop-in shape). OPTIONAL always, by hard
 * requirement: nothing here gates a quote, prices a job, or blocks a save.
 * The Eircode hint is advisory copy only — the value saves as typed.
 *
 * These are NOT priced inputs: writes go through plain patch callbacks, never
 * the paired quote writers (town keeps driving grid pricing; these fields
 * serve the driver + the record).
 */
import { useState } from 'preact/hooks';
import type { JobState, OperatorStop } from '../store/state.js';
import { looksLikeEircode } from '../../shared/eircode.js';
import { isTerminalDesignation } from '../../shared/service-schema.js';
import { golfCourseEnds, golfStatedEnds } from '../store/selectors.js';

/**
 * T608 M6a UX BLOCK — a golf COURSE leg has NO drop-off capture, because the
 * course stop deliberately carries no street data: `journeyStops` writes its
 * `addressLine`/`eircode`/`town` as null (a golf course has no street, and those
 * columns describe where the party was collected — the hub row). Rendering live
 * "Drop-off Eircode" / "Drop-off address" inputs here invited Helen to type
 * 'Clubhouse entrance, side gate', echoed it back on the Quote screen, then
 * dropped it on save with no trace — silently, repeatably.
 *
 * Hidden rather than annotated: Owen and Helen left a previous tool for having
 * "too many buttons", so two fewer dead inputs beats one more caveat line.
 *
 * The gate is the SAME function the persist path gates on, never a second
 * hand-written condition — if the two drifted, this would hide fields that WOULD
 * have persisted, or show fields that will not.
 *
 * T906 S1 widened it by exactly the disjunct `journeyStops` itself gained: a job
 * whose golf ends were STATED by the picker takes the same null-capture
 * destination push, so it must hide the same two inputs. Both arms mirror one
 * builder branch, so this stays one condition rather than two agreeing ones.
 *
 * ⛔ NOT keyed on "the destination is a course". That reads as the natural rule
 * and is wrong three ways: this component has no registry to ask, a name can be
 * BOTH a hub and a course (Druids Glen Resort), and — the one that bites — an
 * off-list pair would then show a drop-off capture that the builder discards,
 * which is verbatim the defect the block above exists to prevent, reintroduced
 * for the whole population this milestone creates.
 */
const hidesDropoffCapture = (j: JobState): boolean =>
  golfStatedEnds(j) !== null || golfCourseEnds(j) !== null;

export type DriverEndPatch = Partial<Pick<JobState,
  'pickupEircode' | 'pickupAddressLine' | 'dropoffEircode' | 'dropoffAddressLine'>>;

export type DriverDetailsProps = {
  /** Manual-mode services (wedding, private tour) skip the Location screen, so
   *  this block is their ONLY pickup capture. Set on those paths to prompt for
   *  a missing pickup. Advisory only - it never blocks or validates. */
  emphasisePickup?: boolean;
  job: JobState;
  /** Render the per-stop rows (Addons — where the extra-pickups count lives).
   *  The manual path passes false: per-end capture only. */
  showStops: boolean;
  /**
   * T660 M9 (P0-28) — put the per-end address fields behind a closed-by-default
   * disclosure. Passed by the ADDONS mount only; both Quote mounts leave it off
   * and are byte-identical to before, which matters because M8 measured that
   * screen for SC-4.
   *
   * ⚠️ Scoped to the per-END fields on purpose. P0-28 and the INITIAL both name
   * "four free-text inputs" — that is exactly the two EndFields pairs, the ones
   * Alex dwelt 29 seconds on. The per-stop rows are ZERO at rest, so they were
   * never among the four; they stay OUTSIDE the disclosure, beside the counter
   * that creates them, which is what this block's placement on Addons is for.
   * Collapsing them too would have set "closed by default" against "capture
   * where the count lives" and forced an exception into one of them.
   */
  collapsible?: boolean;
  onSetEnd: (patch: DriverEndPatch) => void;
  onSetStop: (index: number, patch: Partial<OperatorStop>) => void;
};

/** Copy for the disclosure control, exported so the specs assert one source. */
export const DRIVER_ADDRESS_DISCLOSURE_LABEL = 'Addresses for the driver';

function EircodeHint({ value }: { value: string }) {
  if (value.trim() === '' || looksLikeEircode(value)) return null;
  return (
    <p class="field-hint" role="status">
      Doesn't look like a full Eircode. Saved as typed.
    </p>
  );
}

/**
 * T608 M6b (ux close-out) — say what the bare 'T1' in an address box IS.
 *
 * On an airport lead the terminal end's `address_line` is the designation, and
 * it lands in an ordinary editable field labelled "Pickup address" /
 * "Drop-off address". Unlabelled, a lone 'T1' there is indistinguishable from a
 * typo or a truncated address — so the obvious operator move is to "correct" it
 * to the full terminal name, and that keystroke is a filed trap: on a re-opened
 * arrivals journey `pickupLabelIsStreet` (client/lib/actions.ts) then reads the
 * row as "the label IS the street the operator typed", drops the label, and
 * re-persists the customer's town as the pickup — the airport gone from the
 * record (docs/test-gaps.md, `pickupLabelIsStreet-operator-door`). The writers'
 * guard closes the door a stranger could open; this closes the one the UI was
 * holding open for Helen.
 *
 * A CAPTION, not a control: Owen and Helen left a previous tool for having "too
 * many buttons", so one quiet line and no new affordance. It appears only when
 * the value IS the designation — an ordinary street address gets nothing, which
 * is what keeps this from being one more caveat on every field.
 *
 * The gate is `isTerminalDesignation` (shared/service-schema.ts), the same
 * module the two writers take the designation FROM, so the UI cannot come to
 * disagree with the record about what a terminal code is.
 */
function TerminalHint({ value }: { value: string }) {
  if (!isTerminalDesignation(value)) return null;
  return (
    <p class="field-hint" role="status">
      Terminal code. Leave as is.
    </p>
  );
}

function EndFields({ idBase, label, eircode, addressLine, onEircode, onAddress }: {
  idBase: string;
  label: 'Pickup' | 'Drop-off';
  eircode: string;
  addressLine: string;
  onEircode: (v: string) => void;
  onAddress: (v: string) => void;
}) {
  return (
    <>
      <label class="field-label" for={`${idBase}-eircode`}>{label} Eircode</label>
      <input
        id={`${idBase}-eircode`}
        class="login-input"
        type="text"
        placeholder="A65 F4E2"
        value={eircode}
        onInput={(e) => onEircode((e.target as HTMLInputElement).value)}
      />
      <EircodeHint value={eircode} />
      <label class="field-label" for={`${idBase}-address`}>{label} address</label>
      <input
        id={`${idBase}-address`}
        class="login-input"
        type="text"
        placeholder="Street or house name"
        value={addressLine}
        onInput={(e) => onAddress((e.target as HTMLInputElement).value)}
      />
      <TerminalHint value={addressLine} />
    </>
  );
}

/**
 * T400 item 4 — the READ-ONLY quiet lines for the priced Quote path (capture
 * happened on Addons; here Helen just reads and copies for the driver). Not
 * under the "From the customer" card: these are the WORKING values, which
 * Helen may have edited — that card renders only the customer's statement.
 * Zero interactive chrome: text only, no affordance-budget cost.
 */
export function DriverDetailsLines({ job }: { job: JobState }) {
  const lines: string[] = [];
  const end = (label: string, addressLine: string, eircode: string) => {
    const v = [addressLine.trim(), eircode.trim()].filter(Boolean).join(', ');
    if (v) lines.push(`${label}: ${v}`);
  };
  end('Pickup', job.pickupAddressLine, job.pickupEircode);
  // No drop-off ECHO on a course leg either: this line is what made the discard
  // look like a save (see hidesDropoffCapture above). A pre-existing value from
  // another service is not shown, because it is not what will be recorded.
  if (!hidesDropoffCapture(job)) end('Drop-off', job.dropoffAddressLine, job.dropoffEircode);
  job.operatorStops.slice(0, job.options.extraPickups).forEach((s, i) => {
    const v = [s.address.trim(), s.eircode.trim()].filter(Boolean).join(', ');
    if (v) lines.push(`Stop ${i + 1}: ${v}`);
  });
  if (lines.length === 0) return null;
  return (
    <div class="customer-info" role="group" aria-label="Driver details on record">
      <h2 class="customer-info-title">Driver details</h2>
      {/* ⭐⭐ T1749 M8 round 2 (impeccable P2-2; §3.1's Q139) — `.provenance-line`,
          NOT `.field-hint`. These lines are PROVENANCE: they read back the
          address ON RECORD for each end of the journey ("Pickup: 12 Main St,
          A98 C2D4"), which is what the gold provenance treatment is for. A hint
          says what shape an answer takes, under a field the reader is about to
          fill; there is no field here and nothing to shape. Q139 moved hints off
          gold precisely so that the two stopped looking alike, and this block was
          left wearing the one it is not. */}
      {lines.map((l) => (
        <p key={l} class="provenance-line">{l}</p>
      ))}
    </div>
  );
}

export function DriverDetails(props: DriverDetailsProps) {
  const j = props.job;
  const stopCount = props.showStops ? j.options.extraPickups : 0;
  const rows = Array.from({ length: stopCount }, (_, i) =>
    j.operatorStops[i] ?? { address: '', eircode: '' });
  // Manual-mode services (wedding, private tour) never visit the Location
  // screen, so this block is the ONLY place their pickup is ever captured. A
  // wedding that leaves it blank persists zero booking_stops and reaches the
  // morning as a venue name and nothing else. The prompt stays advisory: a
  // wrong pickup typed to satisfy a required field is worse than none, and it
  // must never stand between Helen and taking a deposit.
  const pickupMissing =
    props.emphasisePickup === true &&
    j.pickupEircode.trim() === '' &&
    j.pickupAddressLine.trim() === '';

  /**
   * Anything already captured in the fields this render would COLLAPSE — the
   * drop-off pair only when it is actually shown, so a stale drop-off value
   * carried in from another service cannot open the panel on a golf leg whose
   * drop-off fields are hidden.
   */
  const hasCapturedEnd =
    j.pickupEircode.trim() !== '' ||
    j.pickupAddressLine.trim() !== '' ||
    (!hidesDropoffCapture(j) &&
      (j.dropoffEircode.trim() !== '' || j.dropoffAddressLine.trim() !== ''));

  /**
   * Closed by default (P0-28) — but OPEN when there is already something in
   * there, which is a re-opened booking.
   *
   * The second half is a relevance rule, not a criterion dodge: hiding an
   * address Helen already captured is the mirror of the harm `hidesDropoffCapture`
   * exists to prevent, and it is the behaviour worth having even if P0-28 had
   * never been written. "Closed by default" is graded on the EMPTY state, which
   * is the state the 29-second dwell was measured in. Both halves are pinned.
   *
   * Initial state only, never an effect, so a re-render inside one mount cannot
   * fight a choice Helen has made.
   *
   * ⚠️ SCOPED HONESTLY, because the first version of this comment was not: it
   * said the screen "unmounts between journeys", which understates how often the
   * state resets. `App.tsx` switches on screen id, so `Addons` unmounts on ANY
   * departure — including the ordinary same-journey `Addons → Quote → Back` hop.
   * So a panel opened by hand and left EMPTY comes back closed. That is the only
   * lossy case and it is benign: the moment anything is typed, `hasCapturedEnd`
   * is true and the panel re-derives OPEN. No captured value is ever hidden by
   * this, which is the property that actually matters. Recorded rather than
   * quietly fixed — moving `endsOpen` into store state to make the choice
   * survive navigation would be a real change, and it is not P0-28's to make.
   */
  const [endsOpen, setEndsOpen] = useState(hasCapturedEnd);
  const collapsed = props.collapsible === true && !endsOpen;

  const endFields = (
    <>
      <EndFields
        idBase="dd-pickup"
        label="Pickup"
        eircode={j.pickupEircode}
        addressLine={j.pickupAddressLine}
        onEircode={(v) => props.onSetEnd({ pickupEircode: v })}
        onAddress={(v) => props.onSetEnd({ pickupAddressLine: v })}
      />
      {/* Drop-off capture, unless the journey's far end is a golf course — which
          records no street data at all (see hidesDropoffCapture above). */}
      {!hidesDropoffCapture(j) && (
        <EndFields
          idBase="dd-dropoff"
          label="Drop-off"
          eircode={j.dropoffEircode}
          addressLine={j.dropoffAddressLine}
          onEircode={(v) => props.onSetEnd({ dropoffEircode: v })}
          onAddress={(v) => props.onSetEnd({ dropoffAddressLine: v })}
        />
      )}
    </>
  );

  return (
    <div class="customer-info" role="group" aria-label="Driver details">
      <h2 class="customer-info-title">Driver details (optional)</h2>
      {pickupMissing && (
        <p class="field-hint">
          No pickup address yet. The driver needs one before the day - add it here, or later from the booking.
        </p>
      )}
      {/* APG Disclosure: the control STAYS PUT and flips aria-expanded (the
          RateList.tsx `aria-expanded={addOpen}` precedent), rather than swapping
          itself out for the panel. Focus is deliberately NOT moved into the
          revealed region — the pattern says not to, and Helen's next keystroke
          should reach the field she chose to open, not be teleported there. */}
      {props.collapsible === true && (
        <button
          type="button"
          class="btn btn--quiet"
          aria-expanded={endsOpen}
          aria-controls="dd-end-fields"
          onClick={() => setEndsOpen(!endsOpen)}
        >
          {DRIVER_ADDRESS_DISCLOSURE_LABEL}
        </button>
      )}
      {props.collapsible === true
        ? <div id="dd-end-fields" hidden={collapsed}>{!collapsed && endFields}</div>
        : endFields}
      {rows.map((row, i) => (
        <div key={i}>
          <label class="field-label" for={`dd-stop-${i + 1}-eircode`}>{`Stop ${i + 1} Eircode`}</label>
          <input
            id={`dd-stop-${i + 1}-eircode`}
            class="login-input"
            type="text"
            placeholder="A65 F4E2"
            value={row.eircode}
            onInput={(e) => props.onSetStop(i, { eircode: (e.target as HTMLInputElement).value })}
          />
          <EircodeHint value={row.eircode} />
          <label class="field-label" for={`dd-stop-${i + 1}-address`}>{`Stop ${i + 1} address`}</label>
          <input
            id={`dd-stop-${i + 1}-address`}
            class="login-input"
            type="text"
            placeholder="Town or address"
            value={row.address}
            onInput={(e) => props.onSetStop(i, { address: (e.target as HTMLInputElement).value })}
          />
        </div>
      ))}
    </div>
  );
}
